import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';
import { ListingCalendarDay, ListingCalendarService } from 'src/shared/services/listing-calendar.service';
import { Lease } from 'src/shared/services/lease.service';

// Every in-month cell reduces to exactly one state, precomputed here so the
// template just switches on a string instead of branching on availability
// itself. `blocked` is an owner block; `booked` is a guest reservation or a
// signed lease holding the night.
type DayState = 'available' | 'booked' | 'blocked';

interface OptimizerCell {
  date: string | null;       // YYYY-MM-DD or null for blanks
  dayNum: number | null;     // 1..31 or null
  shortMonth?: string;       // "Jun" — shown on day 1
  isToday: boolean;
  isWeekend: boolean;
  otherMonth: boolean;       // leading/trailing-month fill
  isPast?: boolean;          // in-month day earlier than today — non-interactive
  data?: ListingCalendarDay; // pricing data when present
  // Computed once per cell so the template doesn't call methods in bindings.
  available?: boolean;       // !blocked && !booked
  minNights?: number;
  maxNights?: number;        // mock-only: no platform supports a max stay yet
  showStayHint?: boolean;
  // The single state driving the cell's background treatment.
  dayState?: DayState;
}

@Component({
  selector: 'optimizer-calendar',
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, TranslationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './optimizer-calendar.component.html',
  styleUrls: ['./optimizer-calendar.component.scss'],
  host: {
    '[class.compact]': 'compact()',
  },
})
export class OptimizerCalendarComponent {
  address = input.required<any>();
  mock = input<boolean>(false);
  // `compact` shrinks row heights and grows the per-day price text — used by
  // the Booking Settings "Price Optimization" card to fit a denser calendar.
  compact = input<boolean>(false);
  // Active leases for the current address — a day inside a lease's term is
  // the calendar being held by a signed legal agreement, so it renders as
  // booked rather than by its reserved-window tier. Optional/defaulted so
  // other callers (e.g. Booking Settings' compact calendar) don't need to
  // wire lease data through.
  leases = input<Lease[]>([]);
  showPricing = input<boolean>(true);
  interactive = input<boolean>(true);
  cellClick = output<ListingCalendarDay>();

  private readonly listingCalendar = inject(ListingCalendarService);
  private readonly cdr = inject(ChangeDetectorRef);

  currentYear: number;
  currentMonth: number;
  cells: OptimizerCell[] = [];

  constructor() {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = now.getMonth() + 1;

    effect(() => {
      // Re-read signals so the effect re-runs when address/mock/leases changes.
      this.address();
      this.mock();
      this.leases();
      this.loadData();
    });
  }

  prevMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 1) {
      this.currentMonth = 12;
      this.currentYear--;
    }
    this.loadData();
  }

  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 12) {
      this.currentMonth = 1;
      this.currentYear++;
    }
    this.loadData();
  }

  /** Re-fetches the current month — called after the day-detail panel writes a block/override. */
  async reload(): Promise<void> {
    // Wait for any in-flight block/override write to settle first so the month
    // refetch reflects it instead of racing ahead of the POST (Trello 18344).
    await this.listingCalendar.whenWritesSettled();
    await this.loadData();
  }

  /** Returns the rendered "Jun 1 - Jun 30" range for the current view. */
  get monthRangeLabel(): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = monthNames[this.currentMonth - 1];
    const last = new Date(this.currentYear, this.currentMonth, 0).getDate();
    return `${m} 1 - ${m} ${last}`;
  }

  onClickCell(cell: OptimizerCell): void {
    if (!this.interactive()) {return;}
    // Past days are read-only — blocking or pricing them is rejected by the
    // channels ("date is in the past"), so don't open the editor (Trello 18343).
    if (cell.isPast) {return;}
    if (cell.data) {this.cellClick.emit(cell.data);}
  }

  private async loadData(): Promise<void> {
    const daysInMonth = new Date(this.currentYear, this.currentMonth, 0).getDate();
    const dateFrom = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-01`;
    const dateTo = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

    let days: ListingCalendarDay[] = [];
    try {
      days = this.mock()
        ? this.buildMockDays(this.currentYear, this.currentMonth, daysInMonth)
        : await this.listingCalendar.getMonth(this.address().id, dateFrom, dateTo);
    } catch (err) {
      // Still render the bare date grid — an unreachable backend shouldn't
      // blank the whole calendar card.
      console.error('Failed to load optimizer calendar data', err);
    }
    const monthPrefix = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-`;
    const monthDays = days.filter(d => d.date.startsWith(monthPrefix));
    this.cells = this.buildCells(monthDays);
    this.cdr.markForCheck();
  }

  private buildCells(monthDays: ListingCalendarDay[]): OptimizerCell[] {
    const firstDow = new Date(this.currentYear, this.currentMonth - 1, 1).getDay();
    const daysInMonth = new Date(this.currentYear, this.currentMonth, 0).getDate();
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const isMock = this.mock();

    // Leading days from previous month
    const prevMonth = this.currentMonth === 1 ? 12 : this.currentMonth - 1;
    const prevYear = this.currentMonth === 1 ? this.currentYear - 1 : this.currentYear;
    const daysInPrev = new Date(prevYear, prevMonth, 0).getDate();

    const cells: OptimizerCell[] = [];

    for (let i = firstDow - 1; i >= 0; i--) {
      const d = daysInPrev - i;
      const dow = new Date(prevYear, prevMonth - 1, d).getDay();
      cells.push({
        date: null,
        dayNum: d,
        isToday: false,
        isWeekend: dow === 0 || dow === 6,
        otherMonth: true,
      });
    }

    const leases = this.leases();
    const byDate = new Map(monthDays.map(d => [d.date, d]));
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dow = new Date(this.currentYear, this.currentMonth - 1, d).getDay();
      let data = byDate.get(key);
      // A date inside an active lease's term is held by a signed legal
      // agreement — it renders as booked, with the nightly price suppressed
      // (leases aren't nightly-priced). Synthesize a stub day if the calendar
      // source didn't return one for this date, so the lease still blocks the
      // cell even with sparse/real calendar data.
      const leased = leases.some(l => key >= l.start_date && key <= l.end_date);
      if (leased) {
        data = { ...(data ?? { date: key, price: 0, currency: 'USD', minStay: 1, isBlocked: false }), isBooked: true };
      }
      const available = data ? !data.isBlocked && !data.isBooked : false;
      // Mock max-stay: weekends prefer shorter caps, weekdays accept longer.
      // Real data has NO max stay (no platform supports one), so the hint
      // shows the min only.
      const isFriOrSat = dow === 5 || dow === 6;
      const maxCap = isFriOrSat ? 7 : 14;
      const maxNights = isMock && data
        ? Math.max((data.minStay ?? 1) + 2, maxCap + ((d * 3) % 5))
        : undefined;
      // Mock: deterministic 'random' ~40% of days show the stay hint so the
      // grid feels lived-in. Real: show it whenever the date has a min stay.
      const showStayHint = isMock
        ? !!data && available && ((d * 17 + this.currentMonth * 7) % 5) < 2
        : !!data && available && data.minStay !== null;
      // A guest reservation (or a lease, which renders as one) wins over an
      // owner block on the same night — it's the stronger claim on the date.
      const dayState: DayState = data?.isBooked
        ? 'booked'
        : data?.isBlocked
        ? 'blocked'
        : 'available';
      cells.push({
        date: key,
        dayNum: d,
        shortMonth: d === 1 ? monthNames[this.currentMonth - 1] : undefined,
        isToday: key === todayKey,
        isPast: key < todayKey,
        isWeekend: dow === 0 || dow === 6,
        otherMonth: false,
        data,
        available,
        minNights: data?.minStay ?? undefined,
        maxNights,
        showStayHint,
        dayState,
      });
    }

    // Trailing days from next month — pad to a multiple of 7.
    let trailing = 1;
    const nextMonth = this.currentMonth === 12 ? 1 : this.currentMonth + 1;
    const nextYear = this.currentMonth === 12 ? this.currentYear + 1 : this.currentYear;
    while (cells.length % 7 !== 0) {
      const dow = new Date(nextYear, nextMonth - 1, trailing).getDay();
      cells.push({
        date: null,
        dayNum: trailing,
        isToday: false,
        isWeekend: dow === 0 || dow === 6,
        otherMonth: true,
      });
      trailing++;
    }

    return cells;
  }

  // Mirrors PricingCalendarComponent's mock so the optimizer demo shows realistic prices.
  private buildMockDays(year: number, month: number, daysInMonth: number): ListingCalendarDay[] {
    // Reservations run as realistic 3-4 night stays with turnover gaps between
    // them — a month of scattered single nights reads as fake. Kept clear of
    // the owner block below.
    const bookedDays = new Set([
      2, 3, 4,          // 3 nights
      7, 8, 9, 10,      // 4 nights
      16, 17, 18,       // 3 nights
      21, 22, 23, 24,   // 4 nights
      28, 29, 30,       // 3 nights
    ]);
    // A short owner block so the demo shows all three calendar states.
    const blockedDays = new Set([12, 13, 14]);
    const days: ListingCalendarDay[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayOfWeek = new Date(year, month - 1, d).getDay();
      const isFriOrSat = dayOfWeek === 5 || dayOfWeek === 6;
      const basePrice = 25000;
      const weekendBoost = isFriOrSat ? 7500 : 0;
      const variance = ((d * 7) % 6) * 1000;
      days.push({
        date: dateStr,
        price: basePrice + weekendBoost + variance,
        currency: 'USD',
        minStay: isFriOrSat ? 2 : 1,
        isBlocked: blockedDays.has(d),
        isBooked: bookedDays.has(d),
      });
    }
    return days;
  }
}
